import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from '@mui/material';
import { Field, FieldProps, Form, Formik } from 'formik';
import { memo, useEffect, useState, useCallback } from 'react';
import {
  useAddFilterConfigMutation,
  useDeleteGridFilterMutation,
  useGetFiltersByUserIdLazyQuery,
  useGetTrafficGridsConfigQuery,
  useUpdateGridFilterMutation,
  useUpdateTrafficGridsConfigMutation,
} from '../generated/graphql';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import * as Yup from 'yup';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import isEqual from 'lodash/isEqual';
import {
  GridColumnVisibilityModel,
  GridDensity,
  GridFilterModel,
  GridPinnedColumns,
  GridSortModel,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import { DialogConfirmationModal } from '../components/DialoConfirmationgModal';
interface ConfigType {
  columnOrder?: string[];
  filterModel?: GridFilterModel;
  sortModel?: GridSortModel;
  pinnedColumns?: GridPinnedColumns;
  colVisibility?: GridColumnVisibilityModel;
  columnsWidth?: any;
  density?: GridDensity;
  expandRowHeight?: boolean;
}

export const GridFiltersDropdown = memo(
  (props: { tableName: string; config: ConfigType; setConfig: any }) => {
    const { tableName, config, setConfig }: any = props;
    const [selectPopupOpen, setSelectPopupOpen] = useState(false);
    const { t } = useTranslation();
    const [addFilterConfig] = useAddFilterConfigMutation();
    const [updateGridConfig] = useUpdateTrafficGridsConfigMutation();
    const { data: gridConfigData, refetch: refetchGridConfig } =
      useGetTrafficGridsConfigQuery();
    const [getFilters] = useGetFiltersByUserIdLazyQuery({
      fetchPolicy: 'no-cache',
    });
    const [deleteGridFilter] = useDeleteGridFilterMutation();
    const [filters, setFilters] = useState<any[]>([]);
    const [options, setOptions] = useState<string[]>([]);
    const [show, setShow] = useState(false);
    const [selectedFilter, setSelectedFilter] = useState({
      filterName: '',
      id: 0,
      filterConfiguration: {},
    });
    const [openConfirmationModal, setOpenConfirmationModal] =
      useState<boolean>(false);
    const [filterNameToDelete, setFilterNameToDelete] = useState<string>('');
    const [filterToUpdate, setFilterToUpdate] = useState<string | null>(null);
    const [openUpdatePopover, setOpenUpdatePopover] = useState(false);

    const validationSchema = Yup.object().shape({
      filterName: Yup.string()
        .required('Name is required')
        .test(
          'unique-name',
          'Name already exists',
          (value) => !options.some((name) => name === value),
        ),
    });
    const [updateFilterConfig] = useUpdateGridFilterMutation();

    //fetch filters, update state, and apply selected filter if needed
    const fetchAndApplyFilters = useCallback(
      async (selectId?: number) => {
        const { data } = await getFilters({ variables: { tableName } });
        const all = data?.getFiltersByUserId || [];
        setFilters([...all]);
        setOptions(all.map((f) => f.filterName));
        let id = selectId;
        if (!id)
          id =
            gridConfigData?.getTrafficGridsConfig?.configuration[tableName]
              ?.userSelectedFilterId;
        if (id) {
          const found = all.find((f) => f.id === id);
          if (found) {
            setSelectedFilter({
              filterName: found.filterName,
              id: found.id,
              filterConfiguration:
                found.filterConfiguration.filters ?? found.filterConfiguration,
            });
            setConfig(
              found.filterConfiguration.filters ?? found.filterConfiguration,
            );
            return;
          }
        }
        setSelectedFilter({ filterName: '', id: 0, filterConfiguration: {} });
        setConfig((prev: ConfigType) => ({
          columnOrder: [],
          filterModel: { items: [] },
          sortModel: [],
          pinnedColumns: { left: [], right: [] },
          colVisibility: prev?.colVisibility,
        }));
      },
      [gridConfigData, tableName],
    );

    useEffect(() => {
      if (gridConfigData) {
        fetchAndApplyFilters();
      }
    }, [gridConfigData, tableName]);

    const setUserSelectedFilterId = async (filterId: number) => {
      // If the initial GetTrafficGridsConfig query has not resolved yet,
      // refetch first so the merge source is the real persisted config and
      // not an empty object. Use the awaited refetch result directly because
      // `gridConfigData` (React state) won't have updated within this async
      // function call.
      let configSource = gridConfigData;
      if (!configSource) {
        try {
          const refetched = await refetchGridConfig();
          configSource = refetched.data;
        } catch (err) {
          // If we can't load the persisted config, abort rather than risk
          // overwriting the user's saved per-table preferences with an
          // object that only contains `userSelectedFilterId`.
          console.error(
            'Aborting userSelectedFilterId update: failed to refetch grid config',
            err,
          );
          return;
        }
      }

      const persistedConfiguration =
        configSource?.getTrafficGridsConfig?.configuration;
      if (!persistedConfiguration) {
        // Same guard as above: missing configuration means we have nothing
        // safe to merge against.
        console.error(
          'Aborting userSelectedFilterId update: persisted grid configuration is unavailable',
        );
        return;
      }

      const existingTableConfig = persistedConfiguration[tableName] ?? {};
      await updateGridConfig({
        variables: {
          input: {
            configuration: {
              ...existingTableConfig,
              userSelectedFilterId: filterId,
            },
            tableName: tableName,
          },
        },
      });
      await refetchGridConfig();
    };

    const onFilterSelection = async (e: any) => {
      const name = e.target.value;
      const selectedUserFilter = filters.find((f) => f.filterName === name);
      if (selectedUserFilter) {
        await setUserSelectedFilterId(selectedUserFilter.id);
        setSelectedFilter({
          id: selectedUserFilter.id,
          filterName: selectedUserFilter.filterName,
          filterConfiguration:
            selectedUserFilter.filterConfiguration.filters ??
            selectedUserFilter.filterConfiguration,
        });
        setConfig(
          selectedUserFilter.filterConfiguration.filters ??
            selectedUserFilter.filterConfiguration,
        );
      } else {
        // Reset all routeConfig filters to their default/empty state
        setSelectedFilter({ id: 0, filterConfiguration: {}, filterName: '' });
        setConfig({
          columnOrder: [],
          filterModel: { items: [] },
          sortModel: [],
          pinnedColumns: { left: [], right: [] },
          colVisibility: { id: false },
          density: 'compact',
        });
        await setUserSelectedFilterId(0);
      }
    };

    const handleDeleteFilter = useCallback(() => {
      const del = filters.find((f) => f.filterName === filterNameToDelete);
      if (del) {
        deleteGridFilter({ variables: { id: del.id } }).then(async () => {
          if (selectedFilter.id === del.id) await setUserSelectedFilterId(0);
          fetchAndApplyFilters();
        });
      }
    }, [filterNameToDelete]);

    const handleUpdateFilter = async () => {
      if (!filterToUpdate) return;

      const userSelectedFilter = filters.find(
        (f) => f.filterName === filterToUpdate,
      );
      if (!userSelectedFilter) return;

      try {
        await updateFilterConfig({
          variables: {
            id: userSelectedFilter.id,
            filterConfiguration: { filters: config },
          },
        });

        await fetchAndApplyFilters(userSelectedFilter.id);
      } catch (err) {
        console.error('Update filter failed', err);
      } finally {
        setFilterToUpdate(null);
        setOpenUpdatePopover(false);
      }
    };

    const onAddFilter = async (values: any) => {
      const res = await addFilterConfig({
        variables: {
          input: {
            filterConfiguration: { filters: config },
            filterName: values.filterName,
            tableName,
          },
        },
      });
      if (res.data?.addFilterConfiguration.id) {
        setShow(false);
        fetchAndApplyFilters(res.data.addFilterConfiguration.id);
      }
    };

    useEffect(() => {
      if (
        selectedFilter.id !== 0 &&
        !isEqual(config, selectedFilter.filterConfiguration)
      ) {
        setSelectedFilter({ filterName: '', id: 0, filterConfiguration: {} });
      }
    }, [config]);

    return (
      <div>
        <Grid
          container
          style={{
            padding: 0,
            width: 'auto',
            flexWrap: 'nowrap',
          }}
          alignItems="center"
          justifyContent="center"
        >
          <Grid item xs={2}>
            <Tooltip title="Save to my filters">
              <IconButton style={{ padding: 0 }} onClick={() => setShow(!show)}>
                <BookmarkAddIcon
                  fontSize="medium"
                  sx={{ color: !filters.length ? undefined : '#32cd32' }}
                />
              </IconButton>
            </Tooltip>
          </Grid>
          {show ? (
            <Grid item xs={10}>
              <Formik
                enableReinitialize
                initialValues={{ filterName: selectedFilter.filterName }}
                validationSchema={validationSchema}
                onSubmit={onAddFilter}
              >
                {({ isValid }) => (
                  <Form>
                    <Grid container alignItems="center" justifyContent="center">
                      <Grid item xs={7}>
                        <Field name="filterName">
                          {({ field, meta }: FieldProps) => (
                            <TextField
                              sx={{ marginLeft: 0 }}
                              {...field}
                              variant="outlined"
                              size="small"
                              label={'Filter name'}
                              type="text"
                              error={Boolean(meta.error)}
                              autoComplete="off"
                            />
                          )}
                        </Field>
                      </Grid>
                      <Grid item xs={5}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={!isValid}
                          size="small"
                        >
                          {t('actions.save')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Form>
                )}
              </Formik>
            </Grid>
          ) : (
            <FormControl
              sx={{ marginRight: '0.5rem', marginLeft: '0.5rem' }}
              size="small"
            >
              <InputLabel id="select-label">{'My filters'}</InputLabel>
              {!show && (
                <Select
                  labelId="filter-list"
                  id="filter-list"
                  value={selectedFilter.filterName ?? ''}
                  sx={{
                    minWidth: 120,
                  }}
                  label={'filters'}
                  onChange={onFilterSelection}
                  open={selectPopupOpen}
                  onOpen={() => setSelectPopupOpen(true)}
                  onClose={() => setSelectPopupOpen(false)}
                >
                  <MenuItem key={'emptyString'} value={''}>
                    <span>None</span>
                  </MenuItem>
                  {options.map((option) => (
                    <MenuItem key={option} value={option}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span style={{ marginTop: '0.5rem' }}>{option}</span>
                        {selectedFilter.filterName !== option &&
                          !option.includes('STND') && (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                edge="end"
                                aria-label="update"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterToUpdate(option);
                                  setOpenUpdatePopover(true);
                                  setSelectPopupOpen(false);
                                }}
                              >
                                <SaveAsIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFilterNameToDelete(option);
                                  setOpenConfirmationModal(true);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              )}
            </FormControl>
          )}
        </Grid>
        <DialogConfirmationModal
          open={openUpdatePopover}
          setOpen={setOpenUpdatePopover}
          contentText={`${t('validation.confirmation', {
            action: t('actions.overrides.lowercased'),
            item: `filter ${filterToUpdate}`,
          })}`}
          doAction={() => handleUpdateFilter()}
          cancelAction={() => {
            setFilterToUpdate(null);
            setOpenUpdatePopover(false);
          }}
          buttonText={t('button.update')}
        />
        <DialogConfirmationModal
          open={openConfirmationModal}
          setOpen={setOpenConfirmationModal}
          contentText={`${t('validation.confirmation', {
            action: t('actions.delete.lowercased'),
            item: `filter ${filterNameToDelete}`,
          })}`}
          doAction={() => handleDeleteFilter()}
          cancelAction={() => setOpenConfirmationModal(false)}
          buttonText={t('button.delete')}
        />
      </div>
    );
  },
);
